import React, { useContext, useEffect } from 'react';
import { VerticalStep, VerticalStepper } from '../components/Stepper';
import {
  Box,
  Button,
  Typography,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Room,
  House,
  CameraAlt,
  Groups,
  InsertDriveFile,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import DialogAction from '../components/Dialog/DialogAction';
import Theme from '../Themes/main.theme';
import PreviewContext from '../components/context/PreviewContext';
import GMapsPlaceMarker from '../components/Maps/GMapsPlaceMarker';
import GMapsPlaceCircle from '../components/Maps/GMapsPlaceCircle';
import { RequestStatus } from '../components/dictionaries/RequestStatus';
import { VisibilityDict } from '../components/dictionaries/LocationVisibility';
import {
  arrayDetailsFormat,
  formatAzureFiles,
  formatMediaData,
  isSubmitEnabled,
  validateLocationCoordinates,
} from '../lib/helperFuncs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import BackButton from '../components/Buttons/BackButton';
import HttpService from '../services/HttpService';
import CardStepContainer from '../components/CardStepsContainer/CardStepContainer';
import Attachments from '../components/Attachments/Attachments';
import UserService from '../services/UserService';
import { IntExt } from '../lib/permissions';
import { Form, Formik, FormikContextType } from 'formik';
import { validationSchemaPreview } from '../lib/static/locations/validationSchema';
import AsyncBackdropSubmit from '../components/AsyncBackdropSubmit';
import TermsDialog from '../components/Dialog/TermsDialog';
import i18n from '../i18n';
import { useApiState } from '../hooks/useApiState';
import { ApiUsability } from '../lib/generalTypes';
import { renderErrors } from '../components/form_v2/helpers';
import CarouselContainerForDetails from '../components/Carousel/CarouselContainerForDetails';

interface IData {
  title: string;
  category: string;
  info: string;
  info_en: string;
  amea_access: boolean | null;
  overweight_vehicle_access: boolean | null;
  restrictions: string;
  restrictions_en: string;
  tags: string[];
  tags_gr: string[];
  show_on_map: 'visible' | 'hidden' | 'sample';
  city: string;
  tk: string;
  address: string;
  address_number: string;
  name: string;
  lastname: string;
  mobile: string;
  email: string;
  website: string;
  latitude: number | null;
  longitude: number | null;
  thumbnail: string;
  media: string[];
  attached_files: string[];
  application_id: number | null;
  licensing: string;
  licensing_en: string;
  preinserted: boolean;
}

const PreviewPage = () => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [media, setMedia] = React.useState<any[]>([]);
  const [checked, setChecked] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const [data, setData] = React.useState<IData>({
    title: '',
    category: '',
    info: '',
    info_en: '',
    amea_access: null,
    overweight_vehicle_access: null,
    restrictions: '',
    restrictions_en: '',
    tags: [],
    tags_gr: [],
    show_on_map: 'hidden',
    city: '',
    tk: '',
    address: '',
    address_number: '',
    name: '',
    lastname: '',
    mobile: '',
    email: '',
    website: '',
    latitude: null,
    longitude: null,
    thumbnail: '',
    media: [],
    attached_files: [],
    application_id: null,
    licensing: '',
    licensing_en: '',
    preinserted: false,
  });
  const { formValues } = useContext(PreviewContext.PreviewProvider.context);
  const { pathname } = useLocation();
  const [termsOpen, setTermsOpen] = React.useState<boolean>(false);

  const formRef = React.useRef<FormikContextType<any>>(null);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const is_external = UserService.hasRole([IntExt.External]);

  const { apiEnabled, loadingApi } = useApiState(
    ApiUsability.Google_Maps,
    true
  );
  const { apiEnabled: geoLocationApi, loadingApi: geoLocationLoading } =
    useApiState(ApiUsability.Geolocation);

  document.title =
    sessionStorage.getItem('mode') !== 'appl_application_edit'
      ? is_external
        ? t('Προεπισκόπηση Αίτηματος Καταχώρισης Ιδιοκτησίας')
        : t('Προεπισκόπηση Εσωτερικού Αίτηματος Καταχώρισης Τοποθεσίας')
      : t('Προεπισκόπηση Αίτηματος Αλλαγής Στοιχείων Ιδιοκτησίας');

  const basic_info_details = [
    {
      name: is_external
        ? t('Τίτλος Ιδιοκτησίας')
        : t('Τίτλος Τοποθεσίας'),
      value: data?.title || '-',
      refValue: 'title',
    },
    {
      name: is_external
        ? t('Κατηγορία Ιδιοκτησίας')
        : t('Κατηγορία Tοποθεσίας'),
      value: data?.category || '-',
      refValue: 'location_category_level_1',
    },
    ...(i18n.language !== 'en'
      ? [
        {
          name: 'Πληροφορίες Ιδιοκτησίας',
          value: data?.info || '-',
          refValue: 'info',
        },
      ]
      : []),
    {
      name: is_external
        ? t('Πληροφορίες Ιδιοκτησίας στα Αγγλικά')
        : t('Πληροφορίες Τοποθεσίας στα Αγγλικά'),
      value: data?.info_en || '-',
      refValue: 'info_en',
    },
    ...(i18n.language !== 'en'
      ? [
        {
          name: t('Πληροφορίες Αδειοδότησης'),
          value: data?.licensing || '-',
          refValue: 'licensing',
        },
      ]
      : []),
    {
      name: t('Πληροφορίες Αδειοδότησης'),
      value: data?.licensing_en || '-',
      refValue: 'licensing_en',
    },
    {
      name: t('Πρόσβαση σε άτομα με ειδικές ανάγκες'),
      value:
        data?.amea_access === null
          ? '-'
          : data?.amea_access
            ? t('ΝΑΙ')
            : t('ΟΧΙ'),
      refValue: 'amea_access',
    },
    {
      name: t('Πρόσβαση σε βαρέα οχήματα'),
      value:
        data?.overweight_vehicle_access === null
          ? '-'
          : data?.overweight_vehicle_access
            ? t('ΝΑΙ')
            : t('ΟΧΙ'),
      refValue: 'overweight_vehicle_access',
    },
    ...(i18n.language !== 'en'
      ? [
        {
          name: t('Περιορισμοί'),
          value: data?.restrictions || '-',
          refValue: 'restrictions',
        },
      ]
      : []),
    {
      name: t('Περιορισμοί στα Αγγλικά'),
      value: data?.restrictions_en || '-',
      refValue: 'restrictions_en',
    },
    {
      name: t('Λέξεις - Κλειδιά'),
      value: data?.tags?.length > 0
        ? data?.tags
        : '-',
      refValue: 'tags',
    },
    ...(i18n.language !== 'en' || !is_external
      ? [
        {
          name: t('Λέξεις - Κλειδιά στα Ελληνικά'),
          value: data?.tags_gr?.length > 0
            ? data?.tags_gr
            : '-',
          refValue: 'tags_gr',
        },
      ]
      : []),
  ];
  const address_info_details = [
    {
      name: t('Εμφάνιση στίγματος στον χάρτη'),
      value: t(VisibilityDict[data?.show_on_map]),
      refValue: 'show_on_map',
    },
    {
      name: t('Περιφέρειες / Δήμοι'),
      value: t(data?.city) || '-',
      refValue: 'region_id',
    },
    {
      name: t('ΤΚ'),
      value: data?.tk
        ? String(data?.tk)
        : '-',
      refValue: 'tk',
    },
    {
      name: t('Οδός'),
      value: data?.address || '-',
      refValue: 'address',
    },
    {
      name: t('Αριθμός'),
      value: data?.address_number || '-',
      refValue: 'address_number',
    },
  ];
  const resp_usr_info_details = [
    ...(is_external
      ? [
        {
          name: t('Όνομα'),
          value: data?.name || '-',
          refValue: 'name',
        },
      ]
      : []),
    ...(is_external
      ? [
        {
          name: t('Επώνυμο'),
          value: data?.lastname || '-',
          refValue: 'lastname',
        },
      ]
      : []),
    {
      name: t('Τηλέφωνο'),
      value: data?.mobile || '-',
      refValue: 'mobile',
    },
    {
      name: 'Email',
      value: data?.email || '-',
      refValue: 'email',
    },
    {
      name: 'Website',
      value: data?.website || '-',
      refValue: 'website',
    },
  ];
  const media_details = [
    {
      name: 'Φωτογραφία Εξωφύλλου',
      value: data?.thumbnail || '-',
      refValue: 'thumbnail',
    },
    {
      name: 'Φωτογραφίες/Βίντεο',
      value: data?.media.join(', '),
      refValue: 'media',
    },
    {
      name: 'Επισυναπτόμενα Έγγραφα',
      value: data?.attached_files.join(', '),
      refValue: 'attachments',
    },
  ];
  const coordinates_details = [
    {
      name: 'Επιβεβαίωση Διεύθυνσης',
      value: String(data?.latitude) || '',
      refValue: 'location',
    },
  ];
  const mediaInfo = (
    <CarouselContainerForDetails media={media} imageButtons={false} />
  );

  const filesInfo =
    data?.attached_files && data?.attached_files?.length > 0
      ? (
        <>
          <Attachments attachments={formatAzureFiles(data?.attached_files)} />
        </>
      )
      : null;

  const steps = [
    {
      label: is_external
        ? t('Βασικά Στοιχεία Ιδιοκτησίας')
        : t('Βασικά Στοιχεία Τοποθεσίας'),
      content: (
        <CardStepContainer
          isLoading={isLoading}
          skeletonLines={basic_info_details.length}
          detailsProps={{
            rows: basic_info_details,
            removeLines: true,
            renderArrays: (array) => arrayDetailsFormat(array),
          }}
        />
      ),
      icon: <House />,
    },
    {
      label: t('Διεύθυνση'),
      content: (
        <Grid container spacing={{ xs: 1 }}>
          <Grid item xs={12}
            md={5}>
            <CardStepContainer
              isLoading={isLoading}
              skeletonLines={address_info_details.length}
              detailsProps={{ rows: address_info_details, removeLines: true }}
            />
          </Grid>
          {apiEnabled &&
            !loadingApi &&
            geoLocationApi &&
            !geoLocationLoading
            ? (
              <Grid
                item
                xs={12}
                md
                display='flex'
                justifyContent={{ xs: 'start', md: 'center' }}
              >
                <Box sx={{ width: '400px', height: '220px' }}>
                  {data?.show_on_map === 'sample'
                    ? (
                      <GMapsPlaceCircle
                        location={{
                          lat: Number(data?.latitude),
                          lng: Number(data?.longitude),
                        }}
                      />
                    )
                    : (
                      <GMapsPlaceMarker
                        zoom={12}
                        location={
                          data?.show_on_map === 'hidden'
                            ? undefined
                            : {
                              lat: Number(data?.latitude),
                              lng: Number(data?.longitude),
                            }
                        }
                      />
                    )}
                </Box>
              </Grid>
            )
            : null}
        </Grid>
      ),
      icon: <Room />,
    },
    ...(data.media.length > 0 || data.thumbnail
      ? [
        {
          label: t('Φωτογραφίες / Βίντεο'),
          content: <Box>{mediaInfo}</Box>,
          icon: <CameraAlt />,
        },
      ]
      : []),
    {
      label: t('Στοιχεία Υπεύθυνου Επικοινωνίας'),
      content: (
        <CardStepContainer
          isLoading={isLoading}
          skeletonLines={resp_usr_info_details.length}
          detailsProps={{
            rows: resp_usr_info_details,
            removeLines: true,
          }}
        />
      ),
      icon: <Groups />,
    },
    ...(data?.attached_files && data?.attached_files?.length > 0
      ? [
        {
          label: t('Άλλα Επισυναπτόμενα Έγγραφα'),
          content: <Box>{filesInfo}</Box>,
          icon: <InsertDriveFile />,
        },
      ]
      : []),
  ];

  const submitValues = async () => {
    setOpenBackdrop(true);
    try {
      const response = await HttpService.getAxiosClient().put(
        `${process.env.REACT_APP_BACKEND_URL}/applications/locations/${data?.application_id}`,
        {
          status: RequestStatus.In_Progress,
        }
      );
      if (response.status === 200) {
        enqueueSnackbar(t(response.data.message), {
          variant: response.data.action,
        });
        pathname.split('/').filter(Boolean)[0] === 'admin'
          ? navigate('/admin/applications/my-requests')
          : navigate('/my-applications', { replace: true });
      }
    } catch (error: any) {
      if (error.response.data.statusCode === 403) {
        // inform user has no permission not only the generic error handling
        enqueueSnackbar(t('Λυπούμαστε δεν έχετε Άδεια.'), { variant: 'error' });
      } else {
        enqueueSnackbar(
          t(
            'Η υποβολή καταχώρισης αιτήματος ιδιοκτησίας απέτυχε, παρακαλώ προσπαθήστε ξανά.'
          ),
          { variant: 'error' }
        );
      }
      console.error(error);
    }
    setOpenBackdrop(false);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleClickOpen = () => {
    setTermsOpen(true);
  };

  const getData = async (id: number) => {
    try {
      const resp = await HttpService.getAxiosClient().get(
        `${process.env.REACT_APP_BACKEND_URL}/locations/details/${id}`
      );
      setData(resp.data);
      const { images, videos, thumbnail } = formatMediaData(resp.data);

      setMedia([...(thumbnail
        ? [thumbnail]
        : []), ...images, ...videos]);
      setIsLoading(false);
    } catch (error) {
      enqueueSnackbar(
        t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'),
        { variant: 'error' }
      );
      console.error(error);
    }
  };

  useEffect(() => {
    const location_id = formValues.id || sessionStorage.getItem('location_id');
    if (location_id) {
      getData(Number(location_id));
    } else navigate('/location/new');
  }, []);

  geoLocationApi &&
    !geoLocationLoading &&
    validateLocationCoordinates(
      formRef,
      data?.latitude
        ? String(data?.latitude)
        : null,
      data?.longitude
        ? String(data?.longitude)
        : null
    );

  return (
    <>
      <AsyncBackdropSubmit isLoading={openBackdrop}>
        {t('Παρακαλώ περιμένετε μέχρι να ολοκληρωθεί η διαδικασία...')}
      </AsyncBackdropSubmit>
      <Box m={2}>
        <Box mt={2} mb={2}>
          <BackButton
            onClick={() =>
              is_external
                ? navigate(
                  `/location/edit/${sessionStorage.getItem('application_id')}`
                )
                : navigate(
                  `/admin/locations/edit/${sessionStorage.getItem(
                    'application_id'
                  )}`
                )
            }
          />
        </Box>
        <Typography variant='h2' mb={2}
          color={Theme.labelColor?.main}>
          {sessionStorage.getItem('mode') !== 'appl_application_edit'
            ? is_external
              ? t('Προεπισκόπηση Αίτηματος Καταχώρισης Ιδιοκτησίας')
              : t('Προεπισκόπηση Εσωτερικού Αίτηματος Καταχώρισης Τοποθεσίας')
            : t('Προεπισκόπηση Αίτηματος Αλλαγής Στοιχείων Ιδιοκτησίας')}
        </Typography>
        <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
        <VerticalStepper activeStep={0}>
          {steps.map((step) => {
            return (
              <VerticalStep
                active={true}
                key={step.label}
                label={step.label}
                icon={step.icon}
              >
                {step.content}
              </VerticalStep>
            );
          })}
        </VerticalStepper>
        <Box mt={2} ml={2}>
          <FormControlLabel
            control={
              <Checkbox checked={checked} onChange={handleCheckboxChange} />
            }
            label={
              <>
                <Button onClick={handleClickOpen}>
                  {t('Αποδοχή όρων Χρήσης & Ορθότητας Στοιχείων')}
                </Button>
                <TermsDialog
                  open={termsOpen}
                  handleClose={() => setTermsOpen(false)}
                  onAccept={() => {
                    setTermsOpen(false);
                    setChecked(true);
                  }}
                  onDecline={() => setChecked(false)}
                />
              </>
            }
          />
        </Box>
        <Formik
          innerRef={formRef}
          enableReinitialize
          onSubmit={() => undefined}
          initialValues={data}
          validationSchema={validationSchemaPreview(data?.preinserted)}
          validateOnBlur={false}
          validateOnMount={true}
        >
          {(formik) => (
            <>
              <Box ml={2} mt={3}>
                {renderErrors(formik.errors, [
                  ...basic_info_details,
                  ...address_info_details,
                  ...resp_usr_info_details,
                  ...media_details,
                  ...coordinates_details,
                ])}
              </Box>
              <Form>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 5,
                    gap: 2,
                  }}
                >
                  <Button
                    variant='outlined'
                    onClick={() =>
                      is_external
                        ? navigate(
                          `/location/edit/${sessionStorage.getItem(
                            'application_id'
                          )}`
                        )
                        : navigate(
                          `/admin/locations/edit/${sessionStorage.getItem(
                            'application_id'
                          )}`
                        )
                    }
                  >
                    {t('Επιστροφή')}
                  </Button>
                  <Button
                    variant='contained'
                    disabled={
                      isSubmitEnabled(formik?.errors, true) ||
                      !checked ||
                      (geoLocationApi && !geoLocationLoading
                        ? !data?.latitude || !data?.longitude
                        : false)
                    }
                    onClick={() => setOpen(true)}
                  >
                    {t('Υποβολή')}
                  </Button>
                </Box>
              </Form>
            </>
          )}
        </Formik>

        <DialogAction
          open={open}
          handleClose={() => setOpen(false)}
          title={`${t('Επιβεβαίωση Υποβολής Αίτησης')}`}
          content={
            sessionStorage.getItem('mode') !== 'appl_application_edit'
              ? is_external
                ? t(
                  'Είστε σίγουρος ότι θέλετε να υποβάλετε την αίτηση καταχώρισης ιδιοκτησίας;'
                )
                : t(
                  'Είστε σίγουρος ότι θέλετε να υποβάλετε την αίτηση καταχώρισης τοποθεσίας;'
                )
              : t(
                'Είστε σίγουρος ότι θέλετε να υποβάλετε την αίτηση αλλαγής στοιχείων ιδιοκτησίας;'
              )
          }
          onAccept={() => {
            submitValues();
            setOpen(false);
          }}
        />
      </Box>
    </>
  );
};

export default PreviewPage;
